import type { Anotacao } from "../types";

/**
 * Desenho das marcas sobre uma print — funções PURAS (recebem as marcas, devolvem HTML).
 *
 * Vive fora do `widget.ts` porque hoje há DOIS consumidores: o fluxo de captura, que
 * desenha o que a pessoa está anotando (`this.anotacoes` + a prévia do arraste), e a lupa
 * do acompanhamento, que desenha o que ela anotou no dia do report (DEV-125), vindo do
 * servidor. Enquanto o desenho lia `this.*`, o segundo caso exigiria duplicá-lo — e duas
 * cópias divergem exatamente no que importa aqui: a geometria.
 *
 * O CONTRATO da geometria, que vale para os dois: as coordenadas são frações 0–1 da
 * IMAGEM, e o elemento que recebe as camadas (`.imgwrap`) tem de ser a caixa EXATA da
 * imagem renderizada. As camadas são `position: absolute; inset: 0` do wrap, então qualquer
 * folga entre wrap e imagem (letterbox de `object-fit: contain`, ou `max-height` só na
 * imagem) desloca todas as marcas de uma vez.
 *
 * A cor vem de `--ab-accent` (o acento do relator) pelo atributo de apresentação + `style`:
 * o `style` vence quando o browser entende `color-mix`, e quando não entende sobra o azul
 * de antes, em vez de uma caixa sem preenchimento.
 */

const ACENTO = "#2f6df6";
const ID_SETA = "altbug-ah";

/** Ponto que identifica a marca (âncora do número). */
export function pontoDaMarca(a: Anotacao): [number, number] {
  if (a.tipo === "rect" && a.x2 != null && a.y2 != null) {
    return [Math.min(a.x, a.x2), Math.min(a.y, a.y2)];
  }

  return [a.x, a.y];
}

/** Uma forma. `pin` não desenha nada aqui — ele é o marcador numerado da outra camada. */
export function svgDaMarca(a: Anotacao): string {
  if (a.tipo === "rect" && a.x2 != null && a.y2 != null) {
    const x = Math.min(a.x, a.x2);
    const y = Math.min(a.y, a.y2);
    const w = Math.abs(a.x2 - a.x);
    const h = Math.abs(a.y2 - a.y);
    const pintura = `fill:color-mix(in srgb, var(--ab-accent) 12%, transparent);stroke:var(--ab-accent)`;

    return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="rgba(47,109,246,.12)" stroke="${ACENTO}" style="${pintura}" stroke-width="2" vector-effect="non-scaling-stroke"/>`;
  }
  if (a.tipo === "arrow" && a.x2 != null && a.y2 != null) {
    return `<line x1="${a.x}" y1="${a.y}" x2="${a.x2}" y2="${a.y2}" stroke="${ACENTO}" style="stroke:var(--ab-accent)" stroke-width="2.5" vector-effect="non-scaling-stroke" marker-end="url(#${ID_SETA})"/>`;
  }

  return "";
}

/** Conteúdo do `.layersvg`: o marker da seta + as formas. */
export function svgDasMarcas(marcas: readonly Anotacao[]): string {
  const marker =
    `<defs><marker id="${ID_SETA}" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">` +
    `<path d="M0,0 L6,3 L0,6 Z" fill="${ACENTO}" style="fill:var(--ab-accent)"/></marker></defs>`;

  return marker + marcas.map(svgDaMarca).join("");
}

/**
 * Conteúdo do `.layer`: um pino numerado por marca, posicionado em % do wrap.
 *
 * O número é o índice na lista de TODAS as marcas — inclusive as sem comentário. (A lista
 * "Comentários" que o backend escreve na nota numera só as COM texto, então um report com
 * marca 1 vazia e marca 2 comentada mostra "1." no texto e "2" no pino. Divergência
 * conhecida, do lado de quem escreve a nota.)
 *
 * O comentário NÃO entra aqui: o pino carrega só o número, e o texto vai para o tooltip
 * por `textContent`. Isso é deliberado — o texto vem de um POST anônimo e este HTML é
 * montado por concatenação na página do cliente.
 *
 * E o pino é SEMPRE da cor do relator. Ele já pintou de verde quando a marca tinha
 * comentário, e isso brigava com o vocabulário de cor do widget: verde ali significa
 * SUCESSO (o ícone do envio, o selo "Resolvido"), e uma marca comentada não é sucesso — é uma
 * marca. Sobre a print o efeito era pior ainda, de status. Quem quer saber se há texto
 * toca/passa o mouse no pino: o tooltip diz o comentário ou "Sem comentário".
 */
export function pinosDasMarcas(marcas: readonly Anotacao[]): string {
  return marcas
    .map((a, i) => {
      const [mx, my] = pontoDaMarca(a);

      return `<span class="pin" data-i="${i}" style="left:${mx * 100}%;top:${my * 100}%">${i + 1}</span>`;
    })
    .join("");
}

/**
 * Os BALÕES de comentário, um por marca que TEM texto — visíveis de saída, ancorados no
 * mesmo ponto do pino.
 *
 * Por que de saída, e não no hover: quem abre a print de um report está lendo o que a
 * pessoa escreveu, e um comentário que só existe enquanto o mouse está em cima não é
 * lido — é descoberto. No celular nem isso: `pointerleave` chega quando o dedo levanta.
 * Ver tudo é o estado natural da tela; o clique no pino esconde o que estorva (marcas
 * próximas se sobrepõem, e é para isso que o toggle existe).
 *
 * Marca SEM comentário não ganha balão: a ausência já responde "essa não tem texto", e um
 * "Sem comentário" por marca vazia seria ruído em cima da imagem.
 *
 * O balão nasce VAZIO de propósito. O texto vem de um POST anônimo e vira conteúdo na
 * página de um terceiro: quem o escreve é `textContent`, nunca esta concatenação.
 *
 * `esq`/`dir` empurram a âncora quando a marca está junto da borda — o wrap tem
 * `overflow: hidden` (as marcas não podem vazar dos cantos arredondados), então um balão
 * centrado num pino da beirada seria cortado no meio da frase. `abaixo` faz o mesmo pelo
 * topo. É posição derivada da COORDENADA, sem medir nada: nada a recalcular no resize.
 */
export function baloesDasMarcas(marcas: readonly Anotacao[]): string {
  return marcas
    .map((a, i) => {
      if (!a.comentario?.trim()) return "";
      const [mx, my] = pontoDaMarca(a);
      const lado = mx < 0.2 ? " esq" : mx > 0.8 ? " dir" : "";
      const vertical = my < 0.12 ? " abaixo" : "";

      return `<span class="pin-tip${vertical}${lado}" data-i="${i}" style="left:${mx * 100}%;top:${my * 100}%"></span>`;
    })
    .join("");
}
